"use client"

import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from "react"

export interface ThemePalette {
  id: string
  label: string
  preview: string
}

export const THEME_PALETTES: ThemePalette[] = [
  { id: "ocean", label: "Ocean", preview: "#2563eb" },
  { id: "forest", label: "Forest", preview: "#16a34a" },
  { id: "sunset", label: "Sunset", preview: "#ea580c" },
  { id: "lavender", label: "Lavender", preview: "#9333ea" },
  { id: "slate", label: "Slate", preview: "#475569" },
  { id: "rose", label: "Rose", preview: "#e11d48" },
  { id: "amber", label: "Amber", preview: "#d97706" },
  { id: "emerald", label: "Emerald", preview: "#059669" },
  { id: "midnight", label: "Midnight", preview: "#312e81" },
]

export const FONT_SIZE_MIN = 11
export const FONT_SIZE_MAX = 20
export const FONT_SIZE_DEFAULT = 14

const PALETTE_KEY = "pos-theme-palette"
const DEFAULT_PALETTE = "forest"
const FONT_PX_KEY = "pos-font-size-px"
const LEGACY_FONT_KEY = "pos-font-size"

interface ThemePaletteContextType {
  palette: string
  setPalette: (id: string) => void
  fontSizePx: number
  setFontSizePx: (px: number) => void
  resetFontSize: () => void
}

const ThemePaletteContext = createContext<ThemePaletteContextType | undefined>(undefined)

function clampFontSize(px: number): number {
  return Math.min(FONT_SIZE_MAX, Math.max(FONT_SIZE_MIN, Math.round(px)))
}

function applyFontSizePx(px: number) {
  document.documentElement.style.fontSize = `${clampFontSize(px)}px`
}

function parseSavedFontSizePx(): number {
  if (typeof window === "undefined") return FONT_SIZE_DEFAULT
  const raw = localStorage.getItem(FONT_PX_KEY)
  if (raw != null) {
    const n = Number(raw)
    if (Number.isFinite(n)) return clampFontSize(n)
  }
  const legacy = localStorage.getItem(LEGACY_FONT_KEY)
  if (legacy === "small") return 13
  if (legacy === "large") return 15
  if (legacy === "medium") return 14
  return FONT_SIZE_DEFAULT
}

function applyPalette(id: string) {
  document.documentElement.dataset.theme = id === "default" ? DEFAULT_PALETTE : id
}

export function ThemePaletteProvider({ children }: { children: ReactNode }) {
  const [palette, setPaletteState] = useState<string>(DEFAULT_PALETTE)
  const [fontSizePx, setFontSizePxState] = useState<number>(FONT_SIZE_DEFAULT)

  useEffect(() => {
    const savedPalette = localStorage.getItem(PALETTE_KEY) === "default" ? DEFAULT_PALETTE : localStorage.getItem(PALETTE_KEY) || DEFAULT_PALETTE
    setPaletteState(savedPalette)
    applyPalette(savedPalette)
    const savedPx = parseSavedFontSizePx()
    setFontSizePxState(savedPx)
    applyFontSizePx(savedPx)
  }, [])

  const setPalette = useCallback((id: string) => {
    setPaletteState(id)
    localStorage.setItem(PALETTE_KEY, id)
    applyPalette(id)
  }, [])

  const setFontSizePx = useCallback((px: number) => {
    const next = clampFontSize(px)
    setFontSizePxState(next)
    localStorage.setItem(FONT_PX_KEY, String(next))
    applyFontSizePx(next)
  }, [])

  const resetFontSize = useCallback(() => {
    setFontSizePx(FONT_SIZE_DEFAULT)
  }, [setFontSizePx])

  return (
    <ThemePaletteContext.Provider value={{ palette, setPalette, fontSizePx, setFontSizePx, resetFontSize }}>
      {children}
    </ThemePaletteContext.Provider>
  )
}

export function useThemePalette() {
  const ctx = useContext(ThemePaletteContext)
  if (!ctx) throw new Error("useThemePalette must be used within a ThemePaletteProvider")
  return ctx
}
